import { OidcSecurityService } from "angular-auth-oidc-client";
import { ReplaySubject, Subscription, firstValueFrom, interval } from "rxjs";

import { EventEmitter, Injectable, Signal, computed, inject, linkedSignal, signal } from "@angular/core";
import { Router } from "@angular/router";

import { Customer } from "../apis/networkdashboard/rest/models";
import { CustomerService } from "../apis/networkdashboard/rest/services";
import { ENV } from "../app/app.runtime";
import { OidcAuthenticationConfig } from "../app/auth/types";
import { ApiHelper } from "../helpers/apihelper";
import { SelfserviceDialogType } from "../helpers/self-service/models/types";
import { EMPTY_CONFIGURATION, EMPTY_USER, NWDUser, NWDUserConfig, OidcTokenResult } from "../helpers/types";
import {
  AccessRules,
  RoleCheckResult,
  SuperUserRoles,
  rolesWithEditAccess,
  rulesAreAvailable,
} from "../helpers/userroles";
import { AnalyticsService } from "./analytics.service";
import { AuthenticationCase } from "./authservice.interfaces";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly api = inject(ApiHelper);
  private readonly customersService = inject(CustomerService);
  private readonly oidc = inject(OidcSecurityService);
  private readonly analytics = inject(AnalyticsService);

  public userLoaded: ReplaySubject<boolean> = new ReplaySubject();
  public roleEvent: EventEmitter<{ event: string; need?: string[]; productType?: string }> = new EventEmitter();
  public lastHttpError: ReplaySubject<{ error: { detail?: string }; message?: string }> = new ReplaySubject();
  private userFetched: ReplaySubject<boolean> = new ReplaySubject();

  // State
  public configuration = signal<NWDUserConfig>(EMPTY_CONFIGURATION);
  public currentUser = signal<NWDUser>(EMPTY_USER);
  public customers = signal<Customer[]>([]);
  public selfServiceToken = signal<OidcTokenResult | undefined>(undefined);
  public userToken = signal<string>("");
  public isInviteUser = computed(
    () => (this.currentUser().inviteCustomerIds ?? []).length > 0 && !this.currentUser().customerId,
  );
  public invitedCustomerIds = computed(() => this.currentUser().inviteCustomerIds ?? []);
  public isInvitedToCurrentCustomer = computed(
    () => this.isInviteUser() && this.invitedCustomerIds().includes(this.viewingCustomer().customerId),
  );
  public viewingCustomer = linkedSignal(() => {
    const customerId = localStorage.getItem("viewingCustomerGUID") ?? this.currentUser().customerId;
    const customer = this.customers().find((customer) => customer.customerId === customerId);
    return customer ?? this.currentUserCustomer();
  });

  // Derived user state
  public currentUserCustomer: Signal<Customer> = computed(() => ({
    // This is a customer object constructed from information in the user profile
    // Specifically, it is *not* a customer from our customers list
    customerId: this.currentUser().customerId ?? "",
    name: this.currentUser().organizationName,
    abbreviation: "",
  }));
  public isInfraverantwoordelijke = computed(() => this.currentUser().roles.includes("Infraverantwoordelijke"));

  // Derived token state
  public selfServiceTTokenValidForUpdater: Subscription | undefined = undefined;
  public selfServiceTokenExpiryTime = computed(() => (this.selfServiceToken()?.exp ?? 0) * 1000);
  public selfServiceTokenValidFor = computed(() => this.selfServiceTokenExpiryTime() - this.selfServiceExpirer()); // TODO Implement updater
  public selfServiceStarted = computed(() => this.selfServiceTokenValidFor() > 0);

  public authenticationCase = computed(() => {
    const user = this.currentUser();
    const customerId = user.customerId;
    const roles = [...user.roles].concat(user.teams);

    if (this.userToken() === "") return AuthenticationCase.NotAuthenticated;
    else if (customerId === null && (user.inviteCustomerIds ?? []).length > 0) return AuthenticationCase.ValidUser;
    else if (customerId === "" || customerId === null) return AuthenticationCase.NoCustomer;
    else if ((roles ?? []).length === 0 && !user.organizationHasIcp) return AuthenticationCase.NoIcp;
    else if ((roles ?? []).length === 0 && !this.isSuperUserRO()) return AuthenticationCase.NoRoles;
    else return AuthenticationCase.ValidUser;
  });

  private router = inject(Router);
  private selfServiceExpirer = signal<number>(0);

  constructor() {
    this.userFetched.subscribe(() => {
      switch (this.authenticationCase()) {
        case AuthenticationCase.NotAuthenticated:
          this.router.navigate(["/forbidden"]);
          break;
        case AuthenticationCase.NoCustomer:
          this.router.navigate(["/forbidden"]);
          break;
        case AuthenticationCase.NoRoles:
          // TODO open roles dialog
          this.router.navigate(["/forbidden"]);
          this.roleEvent.emit({ event: "no-roles", need: [] });
          break;
        case AuthenticationCase.NoIcp:
          this.router.navigate(["/forbidden"]);
          break;
        default:
          // AuthenticationCase.ValidUser
          this.userLoaded.next(true);
          break;
      }
    });
  }

  get isSelfServiceEnabled(): boolean {
    return ENV.SELFSERVICE_ENABLED;
  }

  updateSelfserviceTokenStatus(token: OidcTokenResult) {
    this.selfServiceToken.set(token);
    this.selfServiceTTokenValidForUpdater = interval(1000).subscribe(() => {
      this.selfServiceExpirer.set(new Date().getTime());
    });
  }

  clearSelfserviceTokenStatus() {
    this.selfServiceToken.set(undefined);
    this.selfServiceTTokenValidForUpdater?.unsubscribe();
    this.selfServiceTTokenValidForUpdater = undefined;
  }

  initStepup() {
    // using the pathname we can redirect with the Angular router.
    const state = { redirect: window.location.pathname };
    this.oidc.setState(JSON.stringify(state), OidcAuthenticationConfig.SelfService).subscribe(() => {
      this.oidc.authorize(OidcAuthenticationConfig.SelfService);
    });
  }

  logout() {
    this.oidc.logoffLocal(OidcAuthenticationConfig.SelfService);
    this.oidc.logoffAndRevokeTokens(OidcAuthenticationConfig.Default).subscribe(() => {
      // The `auth` session storage key still carries some state after running the
      // above oidc logoff calls.
      sessionStorage.clear();
      localStorage.removeItem("viewingCustomerGUID");
    });
  }

  /**
   * Retrieve basic user info.
   * If the stored access_token is invalid, it is removed from
   * the localStorage, and load() is called again, redirecting
   * the user to the oauth login URL.
   */
  async fetchUser() {
    if (window.location.href.indexOf("error") > -1) {
      return;
    }

    // get the configuration
    this.api.config$().subscribe({
      next: this.configuration.set,
      error: this.handleBackendDown,
    });

    // get the authenticated user info
    // based on this, we'll load customers the user has access to.
    try {
      const user = await firstValueFrom(this.api.me$());
      this.currentUser.set(user);

      // set the viewing customer if it's not set yet
      // to prevent redirect loops when loading the roles dialog.
      this.setViewingCustomerIfEmpty(user.customerId ?? "");

      try {
        const customers = await this.customersService.getAllCustomers();
        this.customers.set(customers);

        const customerId = this.currentUser().customerId || this.customers()?.[0]?.customerId;
        if (customerId) this.trackCustomerIdentity(customerId);
        const viewingCustomerGUID = this.viewingCustomer().customerId;
        this.setViewingCustomer(viewingCustomerGUID || user.customerId || customerId || "");
      } catch {}

      this.userFetched.next(true);
    } catch (err) {
      this.currentUser.set(EMPTY_USER);
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      this.lastHttpError.next(err);
      this.userFetched.next(true);
    }
  }

  __waaait() {
    return new Promise((resolve) => {
      this.userLoaded.subscribe(() => {
        resolve(null);
      });
    });
  }

  setViewingCustomerIfEmpty(guid: string) {
    if (!localStorage.getItem("viewingCustomerGUID")) {
      localStorage.setItem("viewingCustomerGUID", guid);
    }
  }

  switchViewingCustomer(guid: string, redirectLocation: string) {
    this.setViewingCustomer(guid);

    // This section can be removed when all data is fetched reactively based on viewingCustomer().customerId
    if (redirectLocation !== "") {
      window.location.pathname = redirectLocation;
    } else {
      window.location.reload();
    }
  }

  setViewingCustomer(guid: string) {
    const customer = this.customers().find((o) => o.customerId === guid);

    if (customer) {
      this.viewingCustomer.set(customer);
      // also save in localstorage, so models
      // that do not have access to the auth service
      // can find it.
      localStorage.setItem("viewingCustomerGUID", customer.customerId);
    }
  }

  trackCustomerIdentity(customerId: string) {
    const customer = this.customers().find((o) => o.customerId === customerId);
    if (!customer) {
      console.warn(`User's customer not found for id ${customerId}`);
      return;
    }

    this.analytics.setIdentity({
      customerName: customer.name,
      customerId: customer.customerId,
    });
  }

  customerNameForId(guid: string) {
    const customer = this.customers().filter((e) => e.customerId === guid);
    return customer.length ? customer[0].name : "";
  }

  isCurrentOrganisation(guid: string) {
    return guid === this.viewingCustomer().customerId;
  }

  hasPendingStrongAction(type: string) {
    return localStorage.getItem("strong_preauth_action") === type;
  }
  getPendingStrongAction(): SelfserviceDialogType {
    return localStorage.getItem("strong_preauth_action") as SelfserviceDialogType;
  }

  clearPendingStrongAction(type: string) {
    if (this.hasPendingStrongAction(type)) {
      localStorage.removeItem("strong_preauth_action");
      localStorage.removeItem("strong_pending");
    }
  }

  getSelfserviceState() {
    const selfserviceState = localStorage.getItem("selfservice_state");
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    return JSON.parse(selfserviceState);
  }

  requestSelfserviceDialog(type: string) {
    localStorage.setItem("request_selfservice_dialog", type);
  }

  hasRequestedSelfServiceDialog(): string {
    const dialogType = localStorage.getItem("request_selfservice_dialog");
    localStorage.removeItem("request_selfservice_dialog");
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    return dialogType;
  }

  canRequestSelfServiceDialog(): boolean {
    // disabled in config from NWD API
    if (!this.configuration().selfServiceEnabled) {
      return false;
    }

    // disabled in runtime config for NWD
    if (!ENV.SELFSERVICE_ENABLED) {
      return false;
    }

    const roles = rolesWithEditAccess();
    return this.hasRole(roles) && !this.isSuperUserRO();
  }

  handleBackendDown = () => {
    window.location.pathname = "/error";
  };

  isSuperUserRO() {
    return this.hasRole(SuperUserRoles) || this.isMemberOf(SuperUserRoles);
  }

  /**
   * Test if the current authenticated user has any (or all) of the given roles.
   *
   * @param roles List of roles to check
   * @param all If true, all roles must be present
   * @returns boolean
   */
  hasRole(roles: string[], all: boolean = false): boolean {
    if (all) {
      return roles.every(
        (role) => this.currentUser()?.roles?.includes(role) || this.currentUser()?.teams?.includes(role),
      );
    }

    return roles.some((role) => this.currentUser()?.roles?.includes(role) || this.currentUser()?.teams?.includes(role));
  }

  isMemberOf(team: string[]): boolean {
    return team.some((role) => this.currentUser()?.teams?.includes(role));
  }

  getRoleNames() {
    return this.currentUser()?.roles?.join(", ");
  }

  checkRoleAccess(productType: string, action: "view" | "edit"): RoleCheckResult {
    const result: RoleCheckResult = {
      ok: false,
      event: "requirements-not-met",
      need: [],
      productType,
      requestedAction: action,
    };

    // temporary measure to allow users with _only_ an Invite group
    // to access SURFdomeinen
    // @todo remove this when we have a more elaborate mechanism in place.
    const caseInviteDomains = this.isInviteUser() && productType === "domains";

    if (!rulesAreAvailable(productType)) {
      // case: this product type is not specified in the roles list
      return result;
    }

    if (AccessRules[productType][action]) {
      // case: rules are defined for this action
      result.need = AccessRules[productType][action];
      result.ok = this.hasRole(result.need) || this.isMemberOf(result.need) || caseInviteDomains;
    } else {
      // case: no rules defined for this action
      result.ok = true;
    }

    if (result.ok) {
      result.event = "requirements-met";
    }

    return result;
  }
}
